import type { PositionRange } from "readerText/tokens";
import * as readerTokens from "readerText/tokens";
import * as languageMap from "shared/languageMap";
import type ReaderTextStore from "stores/ReaderTextStore";
import ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";
import type { ReaderFooterState } from "views/ReaderFooterView";
import { schedulePopoverReposition } from "views/readerText/ReaderPopoverLayer";

/**
 * The span showing the word at `position`: the whole phrase's when it's in
 * one, as a phrase's words carry no position of their own
 */
export const elementForPosition = (
  readerTextStore: ReaderTextStore | undefined,
  position: number
) => {
  const group = readerTextStore?.groupByPosition.value.get(position);
  const selector =
    group && group.from !== group.to
      ? `#pageText span[data-group="${group.id}"]`
      : `#pageText span[data-p="${position}"]`;
  return document.querySelector<HTMLElement>(selector) ?? undefined;
};

const CONTEXT_TRANSLATION_STRIP_HEIGHT = 34;

type ReaderTextLayoutDeps = {
  getAppRouter: () => any;
  getOptions: () => any;
  getReaderTextStore: () => ReaderTextStore | undefined;
  getReaderViewStore: () => ReaderViewStore | undefined;
  /** Reports layout completion back to the controller (rendered callbacks, reading tracker). */
  pageRendered: () => void;
  setPageTextViewportNoFooter: (noFooter: boolean) => void;
  /** Audio books set up their player as part of each layout pass. */
  setupSyncAudioPlayerIfNecessary: () => void;
  updateDictionarySize: () => void;
  updateFooterState: (footerState: ReaderFooterState) => void;
  updateUserBookPosition: (
    position: number,
    readTo: number | undefined
  ) => void;
};

/**
 * Pagination and page geometry for the reader text.
 *
 * Pagination is fundamentally imperative - a page boundary can only be found
 * by measuring the text Preact rendered - so this is the one module allowed
 * to read span geometry, and only ever by position (span[data-p] /
 * span[data-group] lookups). Everything it learns is written back into the
 * stores; the single direct DOM write is the viewport-bottom style, which is
 * part of the measurement loop itself and must apply synchronously.
 */
export default class ReaderTextLayout {
  private flippingPage = false;
  private initialLayout = true;
  private layoutTimer?: ReturnType<typeof setTimeout>;
  private navButtonsEnabled = true;
  private updateLayoutDisabled = false;
  private viewportCssBottom: number | undefined;

  constructor(private deps: ReaderTextLayoutDeps) {}

  close() {
    clearTimeout(this.layoutTimer);
  }

  setLayoutDisabled(updateLayoutDisabled: boolean) {
    this.updateLayoutDisabled = updateLayoutDisabled;
  }

  areNavigationButtonsEnabled() {
    return this.navButtonsEnabled;
  }

  hasPageTextViewport() {
    return document.getElementById("pageTextViewport") !== null;
  }

  setupReaderMode(options: any) {
    if ("scroll" in options) {
      document.body.classList.add("scrollMode");
      this.setViewportBottom(this.viewportCssBottom);

      const readerViewStore = this.getReaderViewStore();
      const spans = this.getSpanElements();
      readerViewStore.setFirstRange(this.rangeOfElement(spans[0]));
      readerViewStore.setLastRange(
        this.rangeOfElement(spans[spans.length - 1])
      );

      this.hideFooter();
    } else {
      document.body.classList.remove("scrollMode");
      this.showFooter();
      this.setViewportBottom(this.viewportCssBottom);
    }
  }

  finishReaderPageLayout() {
    if (this.hasPageTextViewport()) {
      const firstWordSpan = document.querySelector<HTMLElement>(
        "#pageText span[data-p]"
      );
      this.getReaderViewStore().setFirstRange(
        firstWordSpan ? this.rangeOfElement(firstWordSpan) : undefined
      );

      this.flippingPage = true;
      this.updateLayout();
    }

    this.deps.updateFooterState({
      nextActivated: false,
      previousActivated: false,
    });
  }

  updateLayout = (retainPosition?: number, doNotRemoveFirstLine?: boolean) => {
    const options = this.deps.getOptions();
    const appRouter = this.deps.getAppRouter();
    if (this.updateLayoutDisabled) {
      // This is needed because of a bug on a user's Chrome + XP machine that I can't
      // reproduce, debugging info is here:
      // https://readlang.uservoice.com/admin/tickets/18?tracking_code=83abb22bcdda2f4aa2f96d653df691d5
      return;
    }

    const stripOffset = document.body.classList.contains(
      "showContextTranslationStrip"
    )
      ? CONTEXT_TRANSLATION_STRIP_HEIGHT
      : 0;
    const isRTL =
      languageMap.map[stores.bookModel.book.value.language].direction === "RTL";

    if ("scroll" in options) {
      this.deps.updateFooterState({ hidden: true, marginButtonsHidden: true });

      this.viewportCssBottom = stripOffset;

      this.getReaderViewStore().setTextVisible(true);
      this.setViewportBottom(this.viewportCssBottom);

      schedulePopoverReposition();
      this.deps.pageRendered();
      return;
    } else {
      this.deps.updateFooterState({
        hidden: false,
        isEndOfText: Boolean(options.endOfText),
        isRTL,
        marginButtonsHidden: false,
      });

      // Allow more space at bottom of screen in new gloss mode so that when translating the bottom
      // row of text, it doesn't obscure the buttons at the bottom of the page.
      const glossMode =
        stores.user.state.value.readerTranslationStyle === "glossNew";
      this.viewportCssBottom = 69 + (glossMode ? 20 : 0) + stripOffset;

      if (options.endOfText) {
        this.deps.updateFooterState({
          isEndOfText: true,
          isLastPage: false,
          readAloudVisible: false,
        });
      } else {
        this.deps.updateFooterState({ readAloudVisible: true });
      }
    }

    this.deps.setupSyncAudioPlayerIfNecessary();

    if (stores.bookModel.book.value.youTubeID != null) {
      this.deps.pageRendered();
      return;
    }
    this.navButtonsEnabled = false;
    clearTimeout(this.layoutTimer);
    this.layoutTimer = setTimeout(
      async () => {
        if (this.updateLayoutDisabled) {
          return;
        }

        const pageText = document.getElementById("pageText");
        const pageTextViewport = document.getElementById("pageTextViewport");
        if (!pageText || !pageTextViewport) {
          return;
        }

        // set image height to half screen height max
        const image = pageText.querySelector<HTMLImageElement>("img");
        if (image && image.getBoundingClientRect().height === 0) {
          image.addEventListener("load", () => {
            image.style.height =
              Math.min(
                image.naturalHeight,
                0.3 * document.body.getBoundingClientRect().height
              ) + "px";
            setTimeout(this.updateLayout, 1);
          });
          if (image.complete) {
            image.dispatchEvent(new Event("load"));
          }
          return;
        }

        const pageTextViewportBottom = this.viewportCssBottom;
        this.setViewportBottom(pageTextViewportBottom);
        pageText.style.top = "0px";

        const containerRect = pageText.getBoundingClientRect();
        const elementTop = (element: HTMLElement) =>
          element.getBoundingClientRect().top - containerRect.top;
        const elementHeight = (element: HTMLElement) =>
          element.getBoundingClientRect().height;

        const viewportHeight = this.innerHeight(pageTextViewport);

        const spans = this.getSpanElements();
        let distanceFromBottom = null;
        const readerViewStore = this.getReaderViewStore();

        readerViewStore.setLastRange(
          this.rangeOfElement(spans[spans.length - 1])
        );

        if (options.to) {
          const spansAndSectionBreaks = pageText.querySelectorAll<HTMLElement>(
            "span[data-p], span[data-group], .sectionBreak"
          );
          const lastSpanOrSectionBreak =
            spansAndSectionBreaks[spansAndSectionBreaks.length - 1];
          const textHeight =
            elementTop(lastSpanOrSectionBreak) +
            elementHeight(lastSpanOrSectionBreak);
          let processingOffScreenNodes = true;
          readerViewStore.setFirstRange(
            this.rangeOfElement(
              this.lastSpanWithCondition(
                spans,
                (span) => {
                  if (processingOffScreenNodes) {
                    if (elementTop(span) === 0) {
                      // skip past these, seems the browser is being clever and not assigning them
                      // a position to save CPU
                      return true;
                    } else {
                      processingOffScreenNodes = false;
                    }
                  }

                  /**
                   * This is the y distance between the top of the span and the end of the whole
                   * text block.
                   */
                  const distanceFromEnd = textHeight - elementTop(span);
                  if (distanceFromEnd > viewportHeight) {
                    return false;
                  }
                  return true;
                },
                true
              )
            )
          );

          if (readerViewStore.firstRange.value === undefined) {
            readerViewStore.setFirstRange(this.rangeOfElement(spans[0]));
          }

          appRouter.navigate(
            "library/" +
              options.bookID +
              "/from/" +
              readerViewStore.firstPosition.value,
            { trigger: true, replace: true }
          );
          return;
        } else {
          let bottomLimit = 0;
          const retainElement =
            retainPosition !== undefined
              ? this.elementForPosition(retainPosition)
              : undefined;
          if (retainElement) {
            // If the retained span goes beyond the bottom of the viewport, let's remove one line
            // from the top of the page in order to keep it on the page.
            if (
              elementTop(retainElement) + elementHeight(retainElement) >
                viewportHeight &&
              !doNotRemoveFirstLine
            ) {
              this.removeFirstLineAndUpdate(retainPosition);
              return;
            }

            // The "+1" is to avoid an error in Firefox where (top + height) isn't
            // exactly the same for the translated word and the other words on the line
            bottomLimit = Math.min(
              viewportHeight -
                (elementTop(retainElement) + elementHeight(retainElement) + 1),
              0
            );
          }
          let lastSpan = this.lastSpanWithCondition(spans, (span) => {
            if (!span) {
              return false;
            }

            distanceFromBottom =
              viewportHeight - (elementTop(span) + elementHeight(span));
            if (distanceFromBottom < bottomLimit) {
              return false;
            }
            return true;
          });

          // Check whether there's a section break immediately after the last span. If there is, and
          // it's outside the viewport, then look for a last span that's higher up the page so that
          // the section break will appear after a line of text on the following page.
          const lastSpanPosition = lastSpan
            ? this.rangeOfElement(lastSpan)?.to
            : undefined;

          const potentialSectionBreakButton =
            lastSpanPosition !== undefined
              ? pageText.querySelector<HTMLElement>(
                  `.sectionBreak [data-to="${lastSpanPosition}"]`
                )
              : null;
          const potentialSectionBreak =
            potentialSectionBreakButton?.closest<HTMLElement>(".sectionBreak");

          if (potentialSectionBreak) {
            const innerDistanceFromBottom =
              viewportHeight -
              elementTop(potentialSectionBreak) -
              elementHeight(potentialSectionBreak);

            if (innerDistanceFromBottom < bottomLimit && lastSpan) {
              const top = elementTop(lastSpan);

              // Find the last span that's higher up the page
              const higherLastSpan = this.lastSpanWithCondition(
                spans,
                (span) => {
                  if (!span) {
                    return false;
                  }

                  if (elementTop(span) >= top) {
                    // We don't want to go as far down the page as we did before.
                    return false;
                  }

                  distanceFromBottom =
                    viewportHeight - (elementTop(span) + elementHeight(span));
                  if (distanceFromBottom < bottomLimit) {
                    return false;
                  }
                  return true;
                }
              );

              // Only shorten the page if that keeps the just-translated span
              // visible. Otherwise its line would be clipped off the page and
              // the word would vanish (forum 5172) — in that case keep the
              // page as it is and let the section break start the next page.
              if (
                !retainElement ||
                (higherLastSpan &&
                  elementTop(higherLastSpan) >= elementTop(retainElement))
              ) {
                lastSpan = higherLastSpan;
              }
            }
          }

          readerViewStore.setLastRange(
            lastSpan ? this.rangeOfElement(lastSpan) : undefined
          );

          if (lastSpan) {
            const sectionBreaks =
              pageText.querySelectorAll<HTMLElement>(".sectionBreak");
            const lastSpanTop = elementTop(lastSpan);

            /** The viewport will be adjusted so that this element fits within the page */
            let adjustToElement: HTMLElement = lastSpan;

            if (sectionBreaks.length > 0) {
              const firstSectionBreak = sectionBreaks[0];

              const innerDistanceFromBottom =
                viewportHeight -
                elementTop(firstSectionBreak) -
                elementHeight(firstSectionBreak);

              // If first section break is both after the last span, and within the viewport, then
              // adjust viewport to that instead
              if (
                elementTop(firstSectionBreak) > lastSpanTop &&
                innerDistanceFromBottom > bottomLimit
              ) {
                adjustToElement = firstSectionBreak;
              }
            }

            // Adjust height to be at bottom of the adjustToElement.
            // Important note: adjustToElement shouldn't have a top margin, since it could result in
            // it being clipped.
            this.setViewportBottom(
              (pageTextViewportBottom ?? 0) +
                (viewportHeight -
                  (elementTop(adjustToElement) +
                    2 +
                    elementHeight(adjustToElement)))
            );
          }
        }

        /** These are added so that we don't reach 100% until we reach the end page. */
        const wordsForEndPage = 50;

        const { percentage, location, totalLocations } = (() => {
          const startOfPage = readerViewStore.firstPosition.value ?? 0;
          const endOfPage = readerViewStore.lastPosition.value ?? 0;
          const totalWords =
            stores.bookModel.book.value.totalWords + wordsForEndPage;
          return {
            percentage: options.endOfText
              ? 100
              : Math.round(
                  (100 * startOfPage) / (totalWords - (endOfPage - startOfPage))
                ),
            location: startOfPage,
            totalLocations: totalWords,
          };
        })();

        this.deps.updateFooterState({ location, percentage, totalLocations });

        const entirePageContainer = document.querySelector(
          "#entirePage .container"
        );
        const bookContent = document.getElementById("bookContent");
        if (entirePageContainer && bookContent) {
          const newButtonWidth =
            0 -
            Math.floor(
              0.5 *
                (entirePageContainer.getBoundingClientRect().width -
                  bookContent.getBoundingClientRect().width)
            );

          this.deps.updateFooterState({ marginButtonOffset: newButtonWidth });
        }

        this.deps.updateDictionarySize();

        if (this.flippingPage) {
          this.getReaderViewStore().setTextVisible(true);
          this.flippingPage = false;
        }
        readerViewStore.notifyPageLayoutUpdated();
        schedulePopoverReposition();

        if (!options.endOfText) {
          const lastSpanEndPosition = readerViewStore.lastPosition.value;
          const firstSpanStartPosition = readerViewStore.firstPosition.value;

          this.deps.updateUserBookPosition(
            firstSpanStartPosition,
            lastSpanEndPosition
          );

          const isLastPage = this.onLastPage();
          this.deps.updateFooterState({ isLastPage, isRTL });
        }

        this.deps.pageRendered();
        this.navButtonsEnabled = true;
        this.initialLayout = false;
      },
      // To fix a bug where the bottom line was getting cut off only on the initial page load
      this.initialLayout ? 100 : 1
    );
  };

  onLastPage() {
    const readerViewStore = this.getReaderViewStore();
    return (
      (readerViewStore.lastPosition.value ?? -1) >=
      stores.bookModel.book.value.totalWords - 1
    );
  }

  private getSpanElements(): HTMLElement[] {
    return Array.from(
      document.querySelectorAll<HTMLElement>(
        "#pageText span[data-p], #pageText span[data-group]"
      )
    );
  }

  private rangeOfElement(
    element: HTMLElement | undefined
  ): PositionRange | undefined {
    if (!element) {
      return undefined;
    }
    const dataPosition = element.getAttribute("data-p");
    if (dataPosition !== null) {
      const position = parseInt(dataPosition, 10);
      const group = this.deps
        .getReaderTextStore()
        ?.groupByPosition.value.get(position);
      return group
        ? { from: group.from, to: group.to }
        : { from: position, to: position };
    }
    const groupId = parseInt(element.getAttribute("data-group") ?? "", 10);
    const group = this.deps.getReaderTextStore()?.groups.value.get(groupId);
    return group ? { from: group.from, to: group.to } : undefined;
  }

  private elementForPosition(position: number): HTMLElement | undefined {
    return elementForPosition(this.deps.getReaderTextStore(), position);
  }

  /** Content-box height (excluding padding), matching the old jQuery .height(). */
  private innerHeight(element: HTMLElement): number {
    const computedStyle = window.getComputedStyle(element);
    return (
      element.clientHeight -
      parseFloat(computedStyle.paddingTop) -
      parseFloat(computedStyle.paddingBottom)
    );
  }

  /**
   * True if the word/group covering `range` lies on the last visible line of
   * the current page and its paragraph continues below the page break, i.e. a
   * drag dwelling on it should pull the next line onto the page. Compares the
   * span's bottom against the last visible span's top, rather than comparing
   * tops, because translated spans start higher than their untranslated
   * neighbours on the same line.
   */
  shouldShiftForRange = (range: PositionRange): boolean => {
    const lastPosition = this.getReaderViewStore().lastPosition.value;
    const lastElement =
      lastPosition !== undefined
        ? this.elementForPosition(lastPosition)
        : undefined;
    const element = this.elementForPosition(range.from);
    if (!element || !lastElement) {
      return false;
    }
    const rect = element.getBoundingClientRect();
    if (rect.bottom <= lastElement.getBoundingClientRect().top) {
      return false;
    }

    // The drag can only extend within `range`'s paragraph segment (crossing a
    // block or a <br> is a soft paragraph boundary the selection can't span).
    // Walk the words after the range: shift only when the next real word -
    // skipping same-line punctuation like a trailing period - already sits
    // below the page break. Dwelling mid-line (e.g. on the first word of the
    // last line, right after a previous nudge revealed it) must not pull in
    // another line, and a paragraph's last line has nothing below to pull in.
    const parsed = this.deps.getReaderTextStore()?.tokens.value;
    const referenceItem = parsed
      ? readerTokens.getWordItem(parsed, range.from)
      : undefined;
    if (!parsed || !referenceItem) {
      return false;
    }
    for (let position = range.to + 1; ; position++) {
      const item = readerTokens.getWordItem(parsed, position);
      if (
        !item ||
        item.blockIndex !== referenceItem.blockIndex ||
        item.brIndex !== referenceItem.brIndex
      ) {
        return false;
      }
      const itemElement = this.elementForPosition(position);
      if (!itemElement) {
        return false;
      }
      if (itemElement.getBoundingClientRect().top >= rect.bottom) {
        return true;
      }
      if (/[\p{L}\p{N}]/u.test(itemElement.textContent ?? "")) {
        return false;
      }
    }
  };

  /**
   * Pulls the next line of text onto the page by removing the top line, so that a
   * drag-selection can continue onto text that was clipped at the page break.
   * Returns false when the page did not shift.
   */
  shiftPageDownOneLine = (): boolean => {
    const options = this.deps.getOptions();
    if (
      "scroll" in options ||
      options.endOfText ||
      !this.navButtonsEnabled ||
      this.updateLayoutDisabled
    ) {
      return false;
    }

    // Only shift when at least one more line of text is clipped below the viewport
    const pageText = document.getElementById("pageText");
    const pageTextViewport = document.getElementById("pageTextViewport");
    const spans = this.getSpanElements();
    const lastSpanInDom = spans[spans.length - 1];
    if (!pageText || !pageTextViewport || !lastSpanInDom) {
      return false;
    }
    if (
      lastSpanInDom.getBoundingClientRect().bottom -
        pageText.getBoundingClientRect().top <=
      this.innerHeight(pageTextViewport)
    ) {
      return false;
    }

    return this.removeFirstLineAndUpdate(undefined);
  };

  /**
   * The viewport-bottom write is part of the measurement loop (the layout is
   * measured against the bottom we just set), so it must apply synchronously -
   * it is written directly to the element rather than through a render pass.
   */
  private setViewportBottom(bottom: number | undefined) {
    const viewport = document.getElementById("pageTextViewport");
    if (viewport && bottom !== undefined) {
      viewport.style.bottom = bottom + "px";
    }
  }

  /**
   * Drops the first line of rendered text by advancing the store's
   * render-from position, then re-runs layout. Used to keep a just-translated
   * bottom-row span on the page when its gloss makes the line overflow, and
   * by the drag line nudge. Returns false when the page structure prevented
   * the shift, so the nudge stops retrying instead of eating the page line by
   * line.
   */
  private removeFirstLineAndUpdate(
    retainPosition: number | undefined
  ): boolean {
    const options = this.deps.getOptions();
    const appRouter = this.deps.getAppRouter();
    const readerViewStore = this.getReaderViewStore();
    const store = this.getReaderTextStore();
    const pageText = document.getElementById("pageText");
    const firstPosition = readerViewStore.firstPosition.value;
    if (!pageText || firstPosition === undefined) {
      return false;
    }

    const firstElement = this.elementForPosition(firstPosition);
    if (!firstElement) {
      return false;
    }
    const containerRect = pageText.getBoundingClientRect();
    const firstSpanTop =
      firstElement.getBoundingClientRect().top - containerRect.top;

    // Find the first span below the first line; everything before it is
    // dropped from the rendered window.
    const spans = this.getSpanElements();
    const newFirstSpan = spans.find(
      (span) =>
        span.getBoundingClientRect().top - containerRect.top > firstSpanTop
    );
    const newFirstRange = this.rangeOfElement(newFirstSpan);
    if (!newFirstRange) {
      return false;
    }

    store.renderFromPosition.value = newFirstRange.from;
    readerViewStore.setFirstRange(newFirstRange);
    appRouter.navigate(
      "library/" + options.bookID + "/from/" + newFirstRange.from,
      { trigger: false, replace: true }
    );

    setTimeout(() => {
      this.updateLayout(retainPosition, true);
    }, 100);
    return true;
  }

  private lastSpanWithCondition(
    spans: HTMLElement[],
    condition: (span: HTMLElement) => boolean,
    backwards?: boolean
  ): HTMLElement | undefined {
    if (spans.length === 0) {
      return undefined;
    }
    let index;
    if (backwards) {
      index = this.binarySearch(spans, condition, spans.length - 1, 0);
    } else {
      index = this.binarySearch(spans, condition, 0, spans.length - 1);
    }
    return spans[index];
  }

  private binarySearch(
    array: HTMLElement[],
    condition: (span: HTMLElement) => boolean,
    start: number,
    end: number
  ): number {
    const diff = Math.abs(end - start);

    if (diff === 0) {
      return start;
    } else if (diff === 1) {
      if (condition(array[end])) {
        return end;
      } else {
        return start;
      }
    } else {
      const mid = Math.round((start + end) / 2);

      if (condition(array[mid])) {
        return this.binarySearch(array, condition, mid, end);
      } else {
        return this.binarySearch(array, condition, start, mid);
      }
    }
  }

  private hideFooter() {
    this.deps.updateFooterState({ hidden: true, marginButtonsHidden: true });
    this.deps.setPageTextViewportNoFooter(true);
    this.updateLayout();
  }

  private showFooter() {
    this.deps.updateFooterState({ hidden: false, marginButtonsHidden: false });
    this.deps.setPageTextViewportNoFooter(false);
    this.viewportCssBottom = document
      .getElementById("footer")
      ?.getBoundingClientRect().height;
    this.updateLayout();
  }

  private getReaderViewStore() {
    const readerViewStore = this.deps.getReaderViewStore();
    if (!readerViewStore) {
      throw new Error("Reader text layout requires a reader view store");
    }
    return readerViewStore;
  }

  private getReaderTextStore() {
    const readerTextStore = this.deps.getReaderTextStore();
    if (!readerTextStore) {
      throw new Error("Reader text layout requires a reader text store");
    }
    return readerTextStore;
  }
}
